import type { Cta, PresetAnchor } from '@cloneai/shared-types';
import { useEffect, useState, type CSSProperties, type JSX, type RefObject } from 'react';

interface CtaOverlayProps {
  ctas: Cta[];
  videoRef: RefObject<HTMLVideoElement | null>;
  onCtaClick: (cta: Cta) => void;
}

/**
 * Absolute-positioned preset anchors mapped to CSS position offsets.
 *
 * Each CTA has a `position: PresetAnchor` on the shared-types schema; the
 * overlay reads this map directly at render time so buttons land in one of the
 * seven well-known slots over the main video.
 */
const ANCHOR_STYLES: Record<PresetAnchor, CSSProperties> = {
  'top-left': { top: 16, left: 16 },
  'top-right': { top: 16, right: 16 },
  'bottom-left': { bottom: 16, left: 16 },
  'bottom-right': { bottom: 16, right: 16 },
  'top-center': { top: 16, left: '50%', transform: 'translateX(-50%)' },
  'bottom-center': { bottom: 16, left: '50%', transform: 'translateX(-50%)' },
  center: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
};

/**
 * Time-triggered CTA overlay.
 *
 * Each CTA has a `trigger.seconds` threshold. While the main intro video
 * plays, every `timeupdate` tick the component recomputes which CTAs are
 * visible (condition: `trigger.seconds <= currentTime`) and renders those as
 * absolutely-positioned buttons anchored by `cta.position`.
 *
 * Once a CTA becomes visible it stays visible until the session ends — the
 * component makes no attempt to hide CTAs if the user somehow scrubs
 * backwards, because the runtime main video has no seek UI.
 *
 * The wrapper is `pointerEvents: 'none'` so the underlying video click-through
 * for the Start button keeps working; only the links themselves re-enable
 * pointer events. HTTP(S) links open in a new tab, while `tel:` links use the
 * current browsing context to launch the device dialler.
 */
export function CtaOverlay({ ctas, videoRef, onCtaClick }: CtaOverlayProps): JSX.Element | null {
  const [visibleIds, setVisibleIds] = useState<Set<string>>(() => new Set<string>());

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = (): void => {
      const t = video.currentTime;
      setVisibleIds((prev) => {
        let changed = false;
        const next = new Set(prev);
        for (const cta of ctas) {
          if (cta.trigger.seconds <= t && !next.has(cta.id)) {
            next.add(cta.id);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [ctas, videoRef]);

  if (ctas.length === 0) return null;
  if (visibleIds.size === 0) return null;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 10,
      }}
    >
      {ctas
        .filter((cta) => visibleIds.has(cta.id))
        .map((cta) => {
          const isTelephone = cta.url.startsWith('tel:');
          return (
            <a
              key={cta.id}
              data-testid={`cta-${cta.id}`}
              href={cta.url}
              target={isTelephone ? undefined : '_blank'}
              rel={isTelephone ? undefined : 'noreferrer noopener'}
              onClick={() => onCtaClick(cta)}
              style={{
                position: 'absolute',
                ...ANCHOR_STYLES[cta.position],
                background: cta.style.bg,
                color: cta.style.fg,
                padding: '8px 18px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
                zIndex: 10,
                pointerEvents: 'auto',
                whiteSpace: 'nowrap',
                textDecoration: 'none',
              }}
            >
              {cta.label}
            </a>
          );
        })}
    </div>
  );
}
